export type AnamnesisContextKind = 'attachment' | 'exam' | 'medication' | 'encounter' | 'patient-summary'

export interface AnamnesisContextMetadataItem {
  id: string
  kind: AnamnesisContextKind
  source: 'future-adapter' | 'manual-contract' | 'system'
  mimeGroup?: 'image' | 'pdf' | 'text' | 'unknown'
  sizeBytes?: number
  createdAt?: string
  tags?: string[]
}

export interface AnamnesisContextMetadata {
  schemaVersion: 1
  enabled: boolean
  items: AnamnesisContextMetadataItem[]
  limits: {
    metadataOnly: true
    multimodalAnalysisActive: false
  }
}

export interface AnamnesisContextAdapter {
  id: string
  enabled: boolean
  collect: () => Promise<AnamnesisContextMetadataItem[]>
}

const SENSITIVE_CONTEXT_KEYS =
  /(text|content|transcript|summary|patient|name|email|phone|document|cpf|cnpj|url|path|filename|fileName)/i

export const isAnamnesisContextAdaptersEnabled = (): boolean =>
  import.meta.env.VITE_SYNAP_ANAMNESIS_CONTEXT_ADAPTERS === 'true'

export const createEmptyAnamnesisContextMetadata = (
  enabled = isAnamnesisContextAdaptersEnabled(),
): AnamnesisContextMetadata => ({
  schemaVersion: 1,
  enabled,
  items: [],
  limits: {
    metadataOnly: true,
    multimodalAnalysisActive: false,
  },
})

const normalizeMimeGroup = (value: unknown): AnamnesisContextMetadataItem['mimeGroup'] => {
  if (value === 'image' || value === 'pdf' || value === 'text' || value === 'unknown') return value
  return undefined
}

export const sanitizeAnamnesisContextMetadataItem = (
  raw: Record<string, unknown>,
): AnamnesisContextMetadataItem | null => {
  const id = typeof raw.id === 'string' ? raw.id.trim() : ''
  const kind = raw.kind
  if (
    !id ||
    (kind !== 'attachment' &&
      kind !== 'exam' &&
      kind !== 'medication' &&
      kind !== 'encounter' &&
      kind !== 'patient-summary')
  ) {
    return null
  }

  const item: AnamnesisContextMetadataItem = {
    id,
    kind,
    source: raw.source === 'manual-contract' || raw.source === 'system' ? raw.source : 'future-adapter',
  }
  const mimeGroup = normalizeMimeGroup(raw.mimeGroup)
  if (mimeGroup) item.mimeGroup = mimeGroup
  if (typeof raw.sizeBytes === 'number' && Number.isFinite(raw.sizeBytes) && raw.sizeBytes >= 0) {
    item.sizeBytes = Math.round(raw.sizeBytes)
  }
  if (typeof raw.createdAt === 'string' && !Number.isNaN(Date.parse(raw.createdAt))) {
    item.createdAt = new Date(raw.createdAt).toISOString()
  }
  if (Array.isArray(raw.tags)) {
    item.tags = raw.tags
      .map((tag) => (typeof tag === 'string' ? tag.trim() : ''))
      .filter((tag) => tag && !SENSITIVE_CONTEXT_KEYS.test(tag))
      .slice(0, 6)
  }
  return item
}

export const sanitizeAnamnesisContextMetadata = (
  rawItems: Array<Record<string, unknown>>,
  enabled = isAnamnesisContextAdaptersEnabled(),
): AnamnesisContextMetadata => ({
  ...createEmptyAnamnesisContextMetadata(enabled),
  items: rawItems
    .map((item) => {
      const safeEntries = Object.entries(item).filter(([key]) => !SENSITIVE_CONTEXT_KEYS.test(key))
      return sanitizeAnamnesisContextMetadataItem(Object.fromEntries(safeEntries))
    })
    .filter((item): item is AnamnesisContextMetadataItem => Boolean(item))
    .slice(0, 20),
})

export const collectAnamnesisContextMetadata = async (
  adapters: AnamnesisContextAdapter[],
): Promise<AnamnesisContextMetadata> => {
  if (!isAnamnesisContextAdaptersEnabled()) return createEmptyAnamnesisContextMetadata(false)
  const collected = await Promise.all(
    adapters.filter((adapter) => adapter.enabled).map((adapter) => adapter.collect()),
  )
  return sanitizeAnamnesisContextMetadata(collected.flat().map((item) => ({ ...item })), true)
}
